// react
import React, { useState, useEffect, useMemo } from "react";

// prop-types
import PropTypes from "prop-types";

// context
import { UserAuth } from "context/AuthContext";

// react-i18next
import { useTranslation } from "react-i18next";

// @mui
import {
  Box,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableContainer,
  TableFooter,
  TablePagination,
  Button,
  IconButton,
  Typography,
  Tooltip,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import {
  KeyboardArrowLeftOutlined as KeyboardArrowLeftOutlinedIcon,
  KeyboardArrowRightOutlined as KeyboardArrowRightOutlinedIcon,
  Add as AddIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  DeleteForever as DeleteForeverIcon,
} from "@mui/icons-material";

// ui-components
import { DatakeyRemoveConfirm } from "ui-components/ORFeedbacks";

// databox-controller
import getTagOwner from "databox-controller/getTagOwner";
import { removeAuthorizedKey } from "databox-controller/manageAuthorizedKey";

// utils
import convertTimestamp from "utils/convertTimestamp";

// A ---------------------------------------------------------------------- M

const EnabledDataKeysTableToolbar = ({ openAddDataKeyDialog, tagOwner }) => {
  const { user } = UserAuth();
  const { t } = useTranslation();

  return (
    <Grid container alignItems="center" spacing={2} mb={2}>
      <Grid item xs={12} sm={6}>
        <Typography variant="body1" fontWeight={700} color="text.secondary">
          {t("datakeys")}
        </Typography>
      </Grid>

      <Grid item xs={12} sm={6} container justifyContent="flex-end" spacing={1}>
        {tagOwner === user.uid && (
          <Grid item sm="auto">
            <Button
              onClick={openAddDataKeyDialog}
              startIcon={<AddIcon />}
              variant="contained"
              sx={{
                borderRadius: 3,
                textTransform: "none",
                fontWeight: 600,
                px: 3,
              }}
            >
              {t("datakey")}
            </Button>
          </Grid>
        )}
      </Grid>
    </Grid>
  );
};

const EnabledDataKeysTableHead = () => {
  const { t } = useTranslation();
  const theme = useTheme();

  const headCells = useMemo(
    () => [
      { id: "name", label: t("name") },
      { id: "timestamp", label: t("addition_datetime") },
      { id: "value", label: t("key") },
      { id: "actions", label: t("actions") },
    ],
    [t]
  );

  return (
    <TableHead sx={{ background: `linear-gradient(90deg, ${theme.palette.primary.main}33, ${theme.palette.primary.dark}11)` }}>
      <TableRow>
        {headCells.map((headCell) => (
          <TableCell key={headCell.id} align={headCell.id !== "actions" ? "left" : "right"} sx={{ fontWeight: 700 }}>
            {headCell.label}
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
};

const TablePaginationActions = ({ count, page, rowsPerPage, onPageChange }) => {
  const theme = useTheme();
  const isRtl = theme.direction === "rtl";

  return (
    <Box sx={{ flexShrink: 0, ml: 2.5 }}>
      <IconButton onClick={(e) => onPageChange(e, page - 1)} disabled={page === 0} aria-label="previous page">
        {isRtl ? <KeyboardArrowRightOutlinedIcon /> : <KeyboardArrowLeftOutlinedIcon />}
      </IconButton>
      <IconButton onClick={(e) => onPageChange(e, page + 1)} disabled={page >= Math.ceil(count / rowsPerPage) - 1} aria-label="next page">
        {isRtl ? <KeyboardArrowLeftOutlinedIcon /> : <KeyboardArrowRightOutlinedIcon />}
      </IconButton>
    </Box>
  );
};

TablePaginationActions.propTypes = {
  count: PropTypes.number.isRequired,
  onPageChange: PropTypes.func.isRequired,
  page: PropTypes.number.isRequired,
  rowsPerPage: PropTypes.number.isRequired,
};

const DataRow = ({ datakey, onRemove }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down("sm"));

  const [showDataKey, setShowDataKey] = useState(false);

  const toggleDataKeyVisibility = () => {
    setShowDataKey(!showDataKey);
  };

  if (isMobile) {
    return (
      <Box
        key={datakey.id}
        sx={{
          p: 2,
          mb: 2,
          mx: 1,
          borderRadius: 3,
          background: `linear-gradient(135deg, ${theme.palette.background.paper}, ${theme.palette.background.default}EE)`,
          border: `1px solid ${theme.palette.divider}`,
          boxShadow: "0 2px 10px rgba(0,0,0,0.2)",
          cursor: "pointer",
          transition: "transform 0.2s ease, box-shadow 0.2s ease",
          "&:hover": {
            transform: "scale(1.01)",
            boxShadow: "0 4px 16px rgba(0,0,0,0.3)",
          },
        }}
      >
        <Typography variant="subtitle1" fontWeight={700}>
          {datakey.name || "N/A"}
        </Typography>

        <Typography variant="body2" color="text.secondary">
          {datakey.added_on ? convertTimestamp(datakey.added_on) : "N/A"}
        </Typography>

        <Box mt={1} sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Typography variant="body2" sx={{ wordBreak: "break-all" }}>
            {showDataKey ? datakey.datakey : "••••••"}
          </Typography>
          <Box display="flex">
            <Tooltip title={showDataKey ? t("hide") : t("show")} placement="top">
              <IconButton onClick={toggleDataKeyVisibility} color="primary">
                {showDataKey ? <VisibilityOffIcon /> : <VisibilityIcon />}
              </IconButton>
            </Tooltip>
            <Tooltip title={t("remove")} placement="top">
              <IconButton onClick={() => onRemove(datakey)} color="error">
                <DeleteForeverIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      </Box>
    );
  }

  return (
    <TableRow>
      <TableCell align="left">{datakey.name || "N/A"}</TableCell>
      <TableCell align="left">{datakey.added_on ? convertTimestamp(datakey.added_on) : "N/A"}</TableCell>
      <TableCell align="left" size="small">
        <span style={{ marginRight: "10px" }}>{showDataKey ? datakey.datakey : "••••••"}</span>
        <Tooltip title={showDataKey ? t("hide") : t("show")} placement="top">
          <IconButton color="primary" onClick={toggleDataKeyVisibility}>
            {showDataKey ? <VisibilityOffIcon /> : <VisibilityIcon />}
          </IconButton>
        </Tooltip>
      </TableCell>
      <TableCell align="right">
        <Tooltip title={t("remove")} placement="top">
          <IconButton color="error" onClick={() => onRemove(datakey)}>
            <DeleteForeverIcon />
          </IconButton>
        </Tooltip>
      </TableCell>
    </TableRow>
  );
};

const EnabledDataKeysTable = ({ tag, dataKeys, openAddDataKeyDialog, handleOpenDatakeyRemovedSuccessul }) => {
  const { user } = UserAuth();
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down("sm"));

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [tagOwner, setTagOwner] = useState();
  const [showRemove, setShowRemove] = useState(false);
  const [selectedDatakey, setSelectedDatakey] = useState(null);

  useEffect(() => {
    const fetchTagOwner = async () => {
      const tagOwner = await getTagOwner(tag.id);
      setTagOwner(tagOwner);
    };
    fetchTagOwner();
  }, [tag]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleShowRemove = (datakey) => {
    setSelectedDatakey(datakey);
    setShowRemove(true);
  };

  const handleRemoveDatakey = async () => {
    try {
      await removeAuthorizedKey(tag.id, selectedDatakey.id, user.uid);
      handleOpenDatakeyRemovedSuccessul();
    } catch (error) {
      console.error("Error in handleRemoveDatakey:", error);
    } finally {
      setShowRemove(false);
    }
  };

  const emptyRows = page > 0 ? Math.max(0, (1 + page) * rowsPerPage - dataKeys.length) : 0;

  return (
    <>
      <Box>
        <EnabledDataKeysTableToolbar openAddDataKeyDialog={openAddDataKeyDialog} tagOwner={tagOwner} />

        <TableContainer
          component={Paper}
          elevation={0}
          sx={{
            borderRadius: 3,
            overflow: "hidden",
            background: `linear-gradient(135deg, ${theme.palette.background.paper}, ${theme.palette.background.default}CC)`,
            backdropFilter: "blur(6px)",
            border: isMobile ? "none" : `1px solid ${theme.palette.divider}`,
          }}
        >
          <Table data-testid="authorized-datakeys-table">
            {!isMobile && <EnabledDataKeysTableHead />}

            <TableBody>
              {(rowsPerPage > 0 ? dataKeys.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage) : dataKeys).map((datakey) => {
                return <DataRow key={datakey.id} datakey={datakey} onRemove={handleShowRemove} />;
              })}
              {!isMobile && emptyRows > 0 && (
                <TableRow style={{ height: 53 * emptyRows }}>
                  <TableCell colSpan={4} />
                </TableRow>
              )}
            </TableBody>

            {!isMobile && (
              <TableFooter>
                <TableRow>
                  <TablePagination
                    rowsPerPageOptions={[10, 20]}
                    count={dataKeys.length}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    labelRowsPerPage={t("rows_per_page")}
                    labelDisplayedRows={({ from, to, count }) => t("displayed_rows", { from, to, count: count !== -1 ? count : `more than ${to}` })}
                    ActionsComponent={TablePaginationActions}
                  />
                </TableRow>
              </TableFooter>
            )}
          </Table>

          {isMobile && (
            <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
              <TablePaginationActions count={dataKeys.length} page={page} rowsPerPage={rowsPerPage} onPageChange={handleChangePage} />
            </Box>
          )}
        </TableContainer>
      </Box>

      <DatakeyRemoveConfirm showRemove={showRemove} setShowRemove={setShowRemove} handleRemoveDatakey={handleRemoveDatakey} />
    </>
  );
};

EnabledDataKeysTable.propTypes = {
  tag: PropTypes.object.isRequired,
  dataKeys: PropTypes.array.isRequired,
  openAddDataKeyDialog: PropTypes.func.isRequired,
};

export default EnabledDataKeysTable;
